/**
 * Shared types, constants, and hooks used by both OrderDetailPanel and
 * VisitDetailPanel. Neither panel renders this file directly — they import
 * from it and extend/compose their own UI on top.
 */

import { useMemo } from 'react';
import type { TaskStatus } from '../../services/api/types.gen';
import type { AvailableClinicianResponse } from '../../services/api/types.gen';
import { parseISOToLocalDate } from '../../utils/dateUtils';

// ---------------------------------------------------------------------------
// Status sets
// ---------------------------------------------------------------------------

export const EDITABLE_STATUSES = new Set<TaskStatus>([
  'not_started',
  'saved',
  'submitted',
  'pending',
  'scheduled',
  'in_progress',
]);

export const SIGNABLE_STATUSES = new Set<TaskStatus>([
  'not_started',
  'saved',
  'submitted',
  'pending_clinician_signature',
  'pending',
  'scheduled',
  'in_progress',
]);

// ---------------------------------------------------------------------------
// Status display labels
// ---------------------------------------------------------------------------

export const STATUS_DISPLAY_LABELS: Record<string, string> = {
  not_started: 'Not Started',
  saved: 'Saved',
  submitted_with_signature: 'Submitted (Signed)',
  submitted: 'Submitted',
  pending_clinician_signature: 'Pending Clinician Signature',
  completed: 'Completed',
  exported: 'Exported',
  cancelled: 'Cancelled',
  sent_to_physician: 'Sent to Physician',
  returned_with_signature: 'Returned with Signature',
};

// ---------------------------------------------------------------------------
// Utility functions
// ---------------------------------------------------------------------------

export function getStatusBadgeVariant(
  status: TaskStatus
): 'default' | 'secondary' | 'outline' | 'destructive' {
  switch (status) {
    case 'completed': // fallthrough
    case 'exported':
      return 'default';
    case 'scheduled':
    case 'submitted_with_signature':
    case 'pending_clinician_signature':
      return 'secondary';
    case 'not_started':
    case 'saved':
    case 'submitted':
      return 'outline';
    case 'cancelled':
      return 'destructive';
    default:
      return 'outline';
  }
}

export const formatDate = (dateString: string | null | undefined): string => {
  const parsed = parseISOToLocalDate(dateString);
  if (!parsed) return 'N/A';
  return parsed.toLocaleDateString();
};

export const formatClinicianName = (c: AvailableClinicianResponse): string => {
  if (c.clinician_first_name && c.clinician_last_name) {
    const creds = c.clinician_credentials ? `, ${c.clinician_credentials}` : '';
    return `${c.clinician_first_name} ${c.clinician_last_name}${creds}`;
  }
  return `${c.user_first_name} ${c.user_last_name}`;
};

// ---------------------------------------------------------------------------
// useTaskPermissions hook
//
// Computes role-based permission flags for sign/approve/break actions.
// Both panels have identical logic — centralised here so changes stay in sync.
// ---------------------------------------------------------------------------

interface UserClinic {
  clinic_id: string;
  is_assignable_clinician: boolean;
  is_case_manager: boolean;
}

interface User {
  id: string;
  user_clinics?: UserClinic[];
}

interface TaskLike {
  status: TaskStatus;
  agency_clinic_id?: string | null;
  assigned_clinician_user_id?: string | null;
}

export interface TaskPermissions {
  hasClinicianRole: boolean;
  hasCaseManagerRole: boolean;
  canSign: boolean;
  canApprove: boolean;
  canBreakSignatures: boolean;
}

export function useTaskPermissions(
  user: User | null | undefined,
  task: TaskLike | null | undefined
): TaskPermissions {
  const hasClinicianRole = useMemo(() => {
    if (!user?.user_clinics?.length || !task?.agency_clinic_id) return false;
    return user.user_clinics.some(
      uc => uc.clinic_id === task.agency_clinic_id && uc.is_assignable_clinician
    );
  }, [user?.user_clinics, task?.agency_clinic_id]);

  const hasCaseManagerRole = useMemo(() => {
    if (!user?.user_clinics?.length || !task?.agency_clinic_id) return false;
    return user.user_clinics.some(
      uc => uc.clinic_id === task.agency_clinic_id && uc.is_case_manager
    );
  }, [user?.user_clinics, task?.agency_clinic_id]);

  const canSign = useMemo(() => {
    if (!user?.id || !task) return false;
    if (!SIGNABLE_STATUSES.has(task.status)) return false;
    if (task.status === 'submitted') return hasCaseManagerRole;
    // For clinician slot: user must be the specific assigned clinician
    if (task.assigned_clinician_user_id) {
      return user.id === task.assigned_clinician_user_id;
    }
    return hasClinicianRole;
  }, [
    task?.status,
    task?.assigned_clinician_user_id,
    user?.id,
    hasClinicianRole,
    hasCaseManagerRole,
  ]);

  const canApprove = useMemo(() => {
    if (!user?.id || !task) return false;
    if (task.status !== 'submitted_with_signature') return false;
    if (
      task.assigned_clinician_user_id &&
      user.id === task.assigned_clinician_user_id
    )
      return false;
    return hasCaseManagerRole;
  }, [
    task?.status,
    task?.assigned_clinician_user_id,
    user?.id,
    hasCaseManagerRole,
  ]);

  const canBreakSignatures = useMemo(() => {
    if (!user?.id || !task) return false;
    if (task.status === 'submitted_with_signature')
      return hasCaseManagerRole || hasClinicianRole;
    if (task.status === 'pending_clinician_signature') return hasClinicianRole;
    return false;
  }, [task?.status, user?.id, hasCaseManagerRole, hasClinicianRole]);

  return {
    hasClinicianRole,
    hasCaseManagerRole,
    canSign,
    canApprove,
    canBreakSignatures,
  };
}
